// The rep's virtuals grid (CEO 2026-10-10): every virtual from one logo, each
// with Approve / Adjust / Find again / Send to designer. Nothing reaches a
// client until the rep approves it — share links, PDFs and downloads carry
// approved virtuals only. Used by the presentation flow and Quick virtual.
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  Download,
  FileDown,
  Link2,
  Loader2,
  MoreHorizontal,
  Move,
  PackagePlus,
  RefreshCw,
  Send,
  Undo2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { cn } from "@/lib/utils";
import {
  type DecorationStyle,
  errorText,
  money,
  saveBlob,
  STYLE_LABEL,
  type Virtual,
  type VirtualSet,
  virtualsApi,
} from "./virtualsApi";

const STATUS: Record<Virtual["status"], { label: string; tone: "active" | "info" | "warn" | "closed" }> = {
  ready: { label: "To review", tone: "info" },
  approved: { label: "Approved", tone: "active" },
  sent_to_designer: { label: "With designer", tone: "warn" },
  rejected: { label: "Rejected", tone: "closed" },
  failed: { label: "Failed", tone: "warn" },
};

export function useVirtualSet(setId: string | null) {
  return useQuery({
    queryKey: ["virtual-set", setId],
    queryFn: () => virtualsApi.getSet(setId!),
    enabled: !!setId,
  });
}

export function SpendMeter() {
  const q = useQuery({ queryKey: ["virtuals-spend"], queryFn: virtualsApi.spend, staleTime: 30_000 });
  if (!q.data) return null;
  const pct = Math.min(100, (q.data.spentCents / Math.max(1, q.data.capCents)) * 100);
  return (
    <div className="min-w-[180px] text-xs text-muted" title="Vision, AI polish and vectorizing share one monthly budget.">
      <div className="flex justify-between gap-3">
        <span>AI budget this month</span>
        <span className="tabular-nums text-ink">{money(q.data.spentCents)} of {money(q.data.capCents)}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-cream-200">
        <div className={cn("h-full rounded-full", pct > 90 ? "bg-risk" : pct > 70 ? "bg-warn" : "bg-ink")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Logo after prep: transparent tile, colour rule, warnings. */
export function LogoPrepCard({ set }: { set: VirtualSet }) {
  const p = set.prep;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-rule bg-white p-3">
      <div
        className="flex h-16 w-28 items-center justify-center rounded-md border border-rule p-1.5"
        style={{ backgroundImage: "repeating-conic-gradient(#eeeeea 0% 25%, #ffffff 0% 50%)", backgroundSize: "12px 12px" }}
      >
        <img src={set.logoUrl} alt="Client logo, background removed" className="max-h-full max-w-full object-contain" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium text-ink">{p.rule.summary}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {p.palette.slice(0, 6).map((h) => (
            <span key={h} className="inline-flex items-center gap-1 rounded border border-rule px-1.5 py-0.5 text-[11px] text-muted">
              <span className="h-2.5 w-2.5 rounded-sm border border-black/10" style={{ background: h }} />
              {h}
            </span>
          ))}
          {p.backgroundRemoved && <span className="text-[11px] text-muted">· background removed</span>}
        </div>
        {p.lowResolution && (
          <p className="flex items-center gap-1.5 text-xs text-warn">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Low resolution — fine for virtuals; vector art needed before production.
          </p>
        )}
      </div>
    </div>
  );
}

export function VirtualsWorkspace(props: {
  set: VirtualSet;
  /** Show "Attach to project" (Quick virtual). */
  allowAttach?: boolean;
  onChanged?: () => void;
  emptyHint?: string;
}) {
  const { set } = props;
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adjusting, setAdjusting] = useState<Virtual | null>(null);
  const [designerFor, setDesignerFor] = useState<Virtual | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const approved = set.virtuals.filter((v) => v.status === "approved").length;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["virtual-set", set.id] });
    await qc.invalidateQueries({ queryKey: ["virtuals-spend"] });
    props.onChanged?.();
  };
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await refresh();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="space-y-4" aria-label="Virtuals">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            Virtuals <span className="text-sm font-normal text-muted tabular-nums">({set.virtuals.length})</span>
          </h2>
          <p className="text-xs text-muted">{approved ? `${approved} approved — only approved virtuals can be shared or downloaded.` : "Review each one. Nothing reaches a client until you approve it."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" disabled={!approved || !!busy} onClick={() => run("pdf", async () => saveBlob(await virtualsApi.pdf(set.id), "virtuals.pdf"))} title={approved ? undefined : "Approve a virtual first"}>
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </Button>
          <Button
            variant="outline"
            disabled={!approved || !!busy}
            onClick={() =>
              run("share", async () => {
                const r = await virtualsApi.share(set.id);
                try { await navigator.clipboard.writeText(r.url); } catch { /* clipboard blocked */ }
                setNotice(`Share link copied: ${r.url} — shows approved virtuals only.`);
              })
            }
            title={approved ? undefined : "Approve a virtual first"}
          >
            <Link2 className="h-4 w-4" /> Share link
          </Button>
          {props.allowAttach && (
            <Button variant="outline" disabled={!approved || !!busy} onClick={() => setAttachOpen(true)}>
              <PackagePlus className="h-4 w-4" /> Attach to project
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-ok/20 bg-ok-bg px-4 py-2.5 text-sm text-ok" role="status">
          <span className="break-all">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-ok/70 hover:text-ok"><X className="h-4 w-4" /></button>
        </div>
      )}
      {error && <p className="rounded-lg border border-risk/20 bg-risk-bg px-4 py-2.5 text-sm text-risk" role="alert">{error}</p>}

      {set.virtuals.length === 0 ? (
        <div className="rounded-lg border border-dashed border-rule bg-white px-5 py-10 text-center text-sm text-muted">{props.emptyHint ?? "No virtuals yet."}</div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {set.virtuals.map((v) => (
            <VirtualCard
              key={v.id}
              v={v}
              busy={busy?.endsWith(v.id) ? busy : null}
              onApprove={(image) => run(`approve:${v.id}`, () => virtualsApi.approve(v.id, image))}
              onUndo={() => run(`reopen:${v.id}`, () => virtualsApi.reopen(v.id))}
              onReject={() => run(`reject:${v.id}`, () => virtualsApi.reject(v.id))}
              onAdjust={() => setAdjusting(v)}
              onRegenerate={(realism) => run(`regen:${v.id}`, () => virtualsApi.regenerate(v.id, realism))}
              onDesigner={() => setDesignerFor(v)}
              onDownload={() => run(`png:${v.id}`, async () => saveBlob(await virtualsApi.png(v.id), `virtual-${v.productName.replace(/[^A-Za-z0-9]+/g, "-")}.png`))}
              onCatalogueRequest={() => run(`cat:${v.id}`, () => virtualsApi.catalogueRequest({ virtual_id: v.id }), "Added to catalogue requests — the team can onboard this product later.")}
            />
          ))}
        </ul>
      )}

      {adjusting && <AdjustDialog v={adjusting} palette={set.prep.palette} onClose={() => setAdjusting(null)} onSaved={refresh} />}
      {designerFor && (
        <DesignerDialog
          v={designerFor}
          onClose={() => setDesignerFor(null)}
          onSend={(note) => run(`designer:${designerFor.id}`, async () => { await virtualsApi.sendToDesigner(designerFor.id, note); setDesignerFor(null); }, "Sent to the designer with the logo, product and spec.")}
        />
      )}
      {attachOpen && (
        <AttachDialog
          onClose={() => setAttachOpen(false)}
          onAttach={(projectId) => run("attach", async () => { const r = await virtualsApi.attach(set.id, projectId); setAttachOpen(false); setNotice(`${r.attached} approved virtual${r.attached === 1 ? "" : "s"} added to the project's files.`); })}
        />
      )}
    </section>
  );
}

function VirtualCard(props: {
  v: Virtual;
  busy: string | null;
  onApprove: (image: "composite" | "realism") => void;
  onUndo: () => void;
  onReject: () => void;
  onAdjust: () => void;
  onRegenerate: (realism: boolean) => void;
  onDesigner: () => void;
  onDownload: () => void;
  onCatalogueRequest: () => void;
}) {
  const { v } = props;
  const [showAi, setShowAi] = useState(!!v.realismUrl);
  const [menu, setMenu] = useState(false);
  useEffect(() => setShowAi(v.approvedImage ? v.approvedImage === "realism" : !!v.realismUrl), [v.realismUrl, v.approvedImage]);
  const src = showAi && v.realismUrl ? v.realismUrl : v.imageUrl;
  const st = STATUS[v.status];
  const size = v.composite.logoSizeIn ? `Logo ${v.composite.logoSizeIn.w}" × ${v.composite.logoSizeIn.h}"` : null;
  const working = !!props.busy;
  const offCatalogue = v.sourceKind !== "catalogue";
  const realismNote = v.realism.status === "rejected" ? `AI polish discarded: ${v.realism.message?.replace(" We kept the exact virtual.", "") ?? "it changed the virtual."} Showing the exact virtual.` : v.realism.status === "failed" ? v.realism.message : null;
  const warnFlags = v.flags.filter((f) => !/^Sample logo removed/.test(f));
  const infoFlags = v.flags.filter((f) => /^Sample logo removed/.test(f));

  return (
    <li className={cn("flex flex-col overflow-hidden rounded-lg border bg-white", v.status === "approved" ? "border-ok/40" : "border-rule")} data-testid="virtual-card">
      <div className="relative aspect-[4/3] bg-canvas">
        {src ? <img src={src} alt={`${v.productName} virtual`} className="h-full w-full object-contain" /> : null}
        {working && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <Loader2 className="h-6 w-6 animate-spin text-ink" aria-label="Working" />
          </div>
        )}
        <div className="absolute left-2 top-2"><StatusPill tone={st.tone}>{st.label}</StatusPill></div>
        {v.realismUrl && (
          <div className="absolute right-2 top-2 flex overflow-hidden rounded border border-rule bg-white text-[11px] font-medium" role="group" aria-label="Which version">
            <button type="button" onClick={() => setShowAi(false)} className={cn("px-2 py-1", !showAi ? "bg-ink text-white" : "text-ink")} aria-pressed={!showAi}>Exact</button>
            <button type="button" onClick={() => setShowAi(true)} className={cn("px-2 py-1", showAi ? "bg-ink text-white" : "text-ink")} aria-pressed={showAi}>AI polish</button>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <p className="text-sm font-semibold text-ink">{v.productName}</p>
          <p className="text-xs text-muted">{[v.colour, STYLE_LABEL[v.style], size].filter(Boolean).join(" · ")}</p>
        </div>
        {warnFlags.length > 0 && (
          <ul className="space-y-1 rounded-md bg-warn-bg px-2.5 py-2 text-[12px] leading-snug text-warn">
            {warnFlags.map((f) => <li key={f} className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden />{f}</li>)}
          </ul>
        )}
        {[...infoFlags, realismNote].filter(Boolean).map((t) => <p key={t!} className="text-[12px] leading-snug text-muted">{t}</p>)}
        <div className="mt-auto flex items-center gap-2 pt-1">
          {v.status === "approved" ? (
            <>
              <span className="inline-flex h-10 items-center gap-1.5 rounded-md bg-ok-bg px-3 text-sm font-medium text-ok"><Check className="h-4 w-4" /> Approved{v.approvedImage === "realism" ? " (AI polish)" : ""}</span>
              <Button variant="ghost" size="icon" onClick={props.onDownload} disabled={working} aria-label="Download PNG" title="Download PNG"><Download className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={props.onUndo} disabled={working}><Undo2 className="h-4 w-4" /> Undo</Button>
            </>
          ) : (
            <>
              <Button onClick={() => props.onApprove(showAi && v.realismUrl ? "realism" : "composite")} disabled={working || !v.imageUrl}>
                <Check className="h-4 w-4" /> Approve
              </Button>
              <Button variant="outline" onClick={props.onAdjust} disabled={working}><Move className="h-4 w-4" /> Adjust</Button>
            </>
          )}
          <div className="relative ml-auto">
            <Button variant="ghost" size="icon" onClick={() => setMenu((m) => !m)} aria-label="More actions" aria-expanded={menu} disabled={working}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {menu && (
              <div className="absolute bottom-11 right-0 z-20 w-60 overflow-hidden rounded-lg border border-rule bg-white py-1 text-sm shadow-lg" role="menu" onMouseLeave={() => setMenu(false)}>
                <MenuItem icon={RefreshCw} onClick={() => { setMenu(false); props.onRegenerate(false); }}>Find the print area again</MenuItem>
                <MenuItem icon={RefreshCw} onClick={() => { setMenu(false); props.onRegenerate(true); }}>Find again + AI polish (≈5¢)</MenuItem>
                <MenuItem icon={Send} onClick={() => { setMenu(false); props.onDesigner(); }}>Send to designer</MenuItem>
                {offCatalogue && <MenuItem icon={PackagePlus} onClick={() => { setMenu(false); props.onCatalogueRequest(); }}>Add to catalogue request</MenuItem>}
                {v.status !== "rejected" && <MenuItem icon={X} onClick={() => { setMenu(false); props.onReject(); }}>Reject</MenuItem>}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function MenuItem(props: { icon: typeof Send; onClick: () => void; children: React.ReactNode }) {
  const Icon = props.icon;
  return (
    <button type="button" role="menuitem" onClick={props.onClick} className="flex w-full items-center gap-2 px-3 py-2 text-left text-ink hover:bg-cream-100">
      <Icon className="h-4 w-4 text-muted" aria-hidden /> {props.children}
    </button>
  );
}

function Modal(props: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && props.onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [props]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={props.title}>
      <div className={cn("max-h-[92vh] w-full overflow-auto rounded-lg bg-white shadow-xl", props.wide ? "max-w-5xl" : "max-w-md")}>
        <div className="flex items-center justify-between border-b border-rule px-5 py-3">
          <h3 className="text-base font-semibold text-ink">{props.title}</h3>
          <button type="button" onClick={props.onClose} aria-label="Close" className="rounded p-1 text-muted hover:bg-cream-100 hover:text-ink"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">{props.children}</div>
      </div>
    </div>
  );
}

/** Drag the print area, size the logo, nudge, pick the ink and the look. Re-drawing is free. */
function AdjustDialog(props: { v: Virtual; palette: string[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const [v, setV] = useState(props.v);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; dx: number; dy: number } | null>(null);
  const [scale, setScale] = useState(Math.round(v.placement.scale * 100));
  const frame = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);

  const save = async (body: Parameters<typeof virtualsApi.nudge>[1]) => {
    setSaving(true);
    setErr(null);
    try {
      setV(await virtualsApi.nudge(v.id, body));
      await props.onSaved();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  const W = v.composite.width ?? 1, H = v.composite.height ?? 1;
  const r = v.composite.imprint;
  const overlay = useMemo(() => {
    if (!r) return null;
    return { left: ((r.cx - r.w / 2) / W) * 100, top: ((r.cy - r.h / 2) / H) * 100, width: (r.w / W) * 100, height: (r.h / H) * 100 };
  }, [r, W, H]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ x0: e.clientX, y0: e.clientY, dx: 0, dy: 0 });
  };
  const onPointerMove = (e: React.PointerEvent) => drag && setDrag({ ...drag, dx: e.clientX - drag.x0, dy: e.clientY - drag.y0 });
  const onPointerUp = () => {
    if (!drag || !frame.current) return setDrag(null);
    const rect = frame.current.getBoundingClientRect();
    const nx = drag.dx / rect.width, ny = drag.dy / rect.height;
    setDrag(null);
    if (Math.abs(nx) < 0.002 && Math.abs(ny) < 0.002) return;
    void save({ area: { box: { ...v.area.box, x: v.area.box.x + nx, y: v.area.box.y + ny }, item: v.area.item, angleDeg: v.area.angleDeg, surface: v.area.surface } });
  };
  const nudge = (dx: number, dy: number) => void save({ placement: { ...v.placement, dx: clamp(v.placement.dx + dx, -0.6, 0.6), dy: clamp(v.placement.dy + dy, -0.6, 0.6) } });
  const onScale = (n: number) => {
    setScale(n);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save({ placement: { ...v.placement, scale: n / 100 } }), 350);
  };
  const inks = Array.from(new Set([...props.palette.slice(0, 4), "#ffffff", "#111111", "#c0c0c0"]));

  return (
    <Modal title={`Adjust — ${v.productName}${v.colour ? ` (${v.colour})` : ""}`} onClose={props.onClose} wide>
      <div className="grid gap-5 md:grid-cols-[1fr_260px]">
        <div ref={frame} className="relative select-none overflow-hidden rounded-md border border-rule bg-canvas" style={{ aspectRatio: `${W} / ${H}` }}>
          {v.imageUrl && <img src={v.imageUrl} alt="" className="absolute inset-0 h-full w-full" draggable={false} />}
          {overlay && (
            <div
              role="slider"
              aria-label="Print area — drag to move"
              aria-valuetext="Drag to move the print area"
              tabIndex={0}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className="absolute cursor-move border-2 border-dashed border-[#2f6fdb] bg-[#2f6fdb]/5"
              style={{ left: `${overlay.left}%`, top: `${overlay.top}%`, width: `${overlay.width}%`, height: `${overlay.height}%`, transform: drag ? `translate(${drag.dx}px, ${drag.dy}px) rotate(${-(r?.angleDeg ?? 0)}deg)` : `rotate(${-(r?.angleDeg ?? 0)}deg)` }}
            >
              <span className="absolute -top-5 left-0 whitespace-nowrap rounded bg-[#2f6fdb] px-1.5 text-[10px] font-medium text-white">{v.estimated ? "Print area (estimated)" : "Print area"}</span>
            </div>
          )}
          {saving && <div className="absolute inset-0 flex items-center justify-center bg-white/50"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        </div>
        <div className="space-y-5 text-sm">
          <p className="text-xs text-muted">Drag the dashed box to move the print area. Changes re-draw instantly and take back any approval.</p>
          <label className="block">
            <span className="mb-1 flex justify-between font-medium text-ink"><span>Logo size</span><span className="tabular-nums text-muted">{scale}%</span></span>
            <input type="range" min={20} max={100} value={scale} onChange={(e) => onScale(Number(e.target.value))} className="w-full accent-ink" />
            <span className="text-xs text-muted">{v.composite.logoSizeIn ? `${v.composite.logoSizeIn.w}" × ${v.composite.logoSizeIn.h}" on the item` : "Share of the print area"}</span>
          </label>
          <div>
            <span className="mb-1 block font-medium text-ink">Nudge</span>
            <div className="grid w-32 grid-cols-3 gap-1">
              <span />
              <Button variant="outline" size="icon" onClick={() => nudge(0, -0.05)} aria-label="Nudge up"><ArrowUp className="h-4 w-4" /></Button>
              <span />
              <Button variant="outline" size="icon" onClick={() => nudge(-0.05, 0)} aria-label="Nudge left"><ArrowLeft className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => void save({ placement: { scale: v.placement.scale, dx: 0, dy: 0 } })} aria-label="Centre"><Move className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => nudge(0.05, 0)} aria-label="Nudge right"><ArrowRight className="h-4 w-4" /></Button>
              <span />
              <Button variant="outline" size="icon" onClick={() => nudge(0, 0.05)} aria-label="Nudge down"><ArrowDown className="h-4 w-4" /></Button>
            </div>
          </div>
          <label className="block">
            <span className="mb-1 block font-medium text-ink">Decoration look</span>
            <select value={v.style} onChange={(e) => void save({ style: e.target.value as DecorationStyle })} className="h-10 w-full rounded-md border border-rule bg-white px-2">
              {(Object.keys(STYLE_LABEL) as DecorationStyle[]).map((s) => <option key={s} value={s}>{STYLE_LABEL[s]}</option>)}
            </select>
          </label>
          {v.style === "one_colour" && (
            <div>
              <span className="mb-1 block font-medium text-ink">Ink color</span>
              <div className="flex flex-wrap gap-1.5">
                {inks.map((h) => (
                  <button key={h} type="button" onClick={() => void save({ ink_hex: h })} aria-label={`Ink ${h}`} aria-pressed={v.inkHex === h} className={cn("h-8 w-8 rounded-md border", v.inkHex === h ? "ring-2 ring-ink ring-offset-1" : "border-rule")} style={{ background: h }} />
                ))}
              </div>
            </div>
          )}
          {err && <p className="text-sm text-risk">{err}</p>}
          <Button className="w-full" onClick={props.onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, n));

function DesignerDialog(props: { v: Virtual; onClose: () => void; onSend: (note: string | null) => void }) {
  const [note, setNote] = useState("");
  return (
    <Modal title="Send to designer" onClose={props.onClose}>
      <p className="mb-3 text-sm text-muted">The designer gets the client's logo, the product photo, the supplier's imprint spec and this virtual. Replies come to you.</p>
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-ink">Note for the designer (optional)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="w-full rounded-md border border-rule p-2" placeholder="e.g. Client wants the logo stacked, white on the navy" />
      </label>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={props.onClose}>Cancel</Button>
        <Button onClick={() => props.onSend(note.trim() || null)}><Send className="h-4 w-4" /> Send</Button>
      </div>
    </Modal>
  );
}

function AttachDialog(props: { onClose: () => void; onAttach: (projectId: string) => void }) {
  const [search, setSearch] = useState("");
  const q = useQuery({
    queryKey: ["virtuals-attach-projects", search],
    queryFn: () => import("@/lib/api").then(({ api }) => api<{ rows: Array<{ id: string; projectNumber?: number; name?: string; title?: string; clientName?: string | null }> }>(`/projects?limit=10${search ? `&search=${encodeURIComponent(search)}` : ""}`)),
  });
  const rows = q.data?.rows ?? [];
  return (
    <Modal title="Attach approved virtuals to a project" onClose={props.onClose}>
      <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects" className="mb-3 h-10 w-full rounded-md border border-rule px-3 text-sm" />
      <ul className="max-h-72 divide-y divide-rule overflow-auto rounded-md border border-rule">
        {rows.map((p) => (
          <li key={p.id}>
            <button type="button" onClick={() => props.onAttach(p.id)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-cream-100">
              <span className="text-ink">{p.name ?? p.title ?? "Project"}{p.projectNumber ? <span className="text-muted"> · #{p.projectNumber}</span> : null}</span>
              {p.clientName && <span className="text-xs text-muted">{p.clientName}</span>}
            </button>
          </li>
        ))}
        {!rows.length && <li className="px-3 py-4 text-sm text-muted">{q.isLoading ? "Loading…" : "No projects found."}</li>}
      </ul>
      <p className="mt-2 text-xs text-muted">They land in the project's Files as proofs.</p>
    </Modal>
  );
}
